import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { MobileDrawer } from "@wix/wix-ui-tpa/cssVars";
import classNames from "classnames";
import { useRef, useMemo, useState, useCallback } from "react";
import { ConfigContext } from "../../../../contexts/config-context.js";
import { useId } from "../../../../hooks/use-id.js";
import { classes as classes$1 } from "../../../../theme/form-styles.st.css";
import { toLongCountryCode } from "../phone-input-utils.js";
import { CountryOption } from "./country-option.js";
import { classes } from "./drawer-with-search.st.css";
import { FlagButton } from "./flag-button.js";
import { Search } from "./search.js";
import { CoreDropdownContent } from "../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownContent/CoreDropdownContent.js";
import { OptionFactory } from "../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownOption/OptionFactory.js";
const DrawerWithSearch = ({
  className,
  theme,
  countryCode,
  countries,
  onSelect,
  searchPhrase,
  onSearch,
  onKeyDown,
  readOnly,
  ariaLabel
}) => {
  const flagButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const longCountryCode = useMemo(
    () => toLongCountryCode(countryCode),
    [countryCode]
  );
  const [isOpen, setIsOpen] = useState(false);
  const { comboboxProps, listboxProps, setActiveDescendant } = useComboxbox();
  const options = useMemo(() => countries.map(createOption), [countries]);
  const initialSelectedIds = useMemo(
    () => countryCode ? [countryCode] : [],
    [countryCode]
  );
  const firstCountryCode = countries[0]?.shortCountryCode;
  const focusOnOption = searchPhrase ? firstCountryCode : countryCode ?? firstCountryCode;
  const handleExpandChange = useCallback(
    (open) => {
      setIsOpen(readOnly ? false : open);
      onSearch("");
    },
    [onSearch, readOnly]
  );
  const handleKeydown = useCallback(
    (event) => {
      switch (event.key) {
        case "Tab":
          event.preventDefault();
          handleExpandChange(false);
          break;
        case "Escape":
          event.preventDefault();
          handleExpandChange(false);
          flagButtonRef.current?.focus();
          break;
        case "Enter":
          event.preventDefault();
          handleExpandChange(false);
          const _countryCode = dropdownRef.current?.getSelectedOption()?.id;
          _countryCode && onSelect(_countryCode);
          break;
        case "ArrowUp":
        case "ArrowDown":
          dropdownRef.current?.onKeyDown(event.key, event);
          break;
      }
      onKeyDown(event);
    },
    [onKeyDown, handleExpandChange, onSelect]
  );
  const handleSelect = useCallback(
    (option) => {
      const _countryCode = option?.id;
      handleExpandChange(false);
      _countryCode && onSelect(_countryCode);
    },
    [onSelect, handleExpandChange]
  );
  const handleSearch = useCallback(
    (event) => onSearch(event.target.value),
    [onSearch]
  );
  const handleOptionHover = useCallback(
    (option) => {
      option && setActiveDescendant(option._DOMid);
    },
    [setActiveDescendant]
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      FlagButton,
      {
        readOnly,
        arrowDirection: isOpen ? "up" : "down",
        longCountryCode,
        onClick: () => handleExpandChange(true),
        ariaLabel
      }
    ),
    /* @__PURE__ */ jsx(ConfigContext.Consumer, { children: (config) => /* @__PURE__ */ jsx(
      MobileDrawer,
      {
        isOpen,
        onRequestClose: () => handleExpandChange(false),
        className: classNames(
          // compId as className adds form app style vars
          config?.compId,
          // compId_ooi as className adds form app style vars in Harmony sites
          // TODO: Might be possible to remove when https://wix.atlassian.net/browse/TB-14333 is fixed
          `${config?.compId}_ooi`,
          classes$1.formStyles,
          classes.drawer
        ),
        children: /* @__PURE__ */ jsx(
          CoreDropdownContent,
          {
            "data-hook": "dropdown-content",
            optionsContainerId: listboxProps.id,
            role: listboxProps.role,
            className: classes.content,
            ref: dropdownRef,
            selectedIds: initialSelectedIds,
            options,
            onOptionClick: handleSelect,
            onOptionHover: handleOptionHover,
            focusOnOption,
            fixedHeader: /* @__PURE__ */ jsx("div", { className: classes.searchContainer, children: /* @__PURE__ */ jsx(
              Search,
              {
                ...comboboxProps,
                className,
                tabIndex: isOpen ? -1 : 0,
                theme,
                value: searchPhrase,
                onChange: handleSearch,
                onKeyDown: handleKeydown
              }
            ) })
          }
        )
      }
    ) })
  ] });
};
function createOption({
  longCountryCode,
  shortCountryCode,
  callingCode,
  countryName
}) {
  return OptionFactory.create({
    id: shortCountryCode,
    render: () => /* @__PURE__ */ jsx(
      CountryOption,
      {
        callingCode,
        longCountryCode,
        countryName,
        clip: false
      }
    )
  });
}
function useComboxbox() {
  const listboxId = useId();
  const [activeDescendant, setActiveDescendant] = useState();
  const comboboxProps = {
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-haspopup": "listbox",
    "aria-expanded": true,
    "aria-owns": listboxId,
    "aria-activedescendant": activeDescendant
  };
  const listboxProps = {
    role: "listbox",
    id: listboxId
  };
  return { comboboxProps, listboxProps, setActiveDescendant };
}
export {
  DrawerWithSearch
};
//# sourceMappingURL=drawer-with-search.js.map
